import { useState, useCallback, useEffect } from 'react';
import { supabase } from '../services/supabaseClient';
import useGlobalData from './useGlobalData';

// Simple request queue to prevent too many concurrent requests
const requestQueue = {
    concurrentRequests: 0,
    maxConcurrent: 4, // Increased concurrent requests limit
    async execute(fn) {
        // Wait if too many requests are already in progress
        while (this.concurrentRequests >= this.maxConcurrent) {
            await new Promise(resolve => setTimeout(resolve, 500));
        }
        
        this.concurrentRequests++;
        try {
            return await fn();
        } finally {
            this.concurrentRequests--;
        }
    }
};

/**
 * Optimizuotas hook'as duomenų gavimui iš Supabase
 * Efektyviau naudoja cache ir išvengia nereikalingų užklausų
 */
export function useSupabaseData(options, defaultValue = []) {
    const { name } = options;
    const { originalData, setOriginalData, isDataLoaded } = useGlobalData();
    const [data, setData] = useState(defaultValue);
    const [isLoading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Duomenų raktas globaliam state
    const dataKey = name === 'atsakymai' ? 'atsakymai' : 
                    name === 'formos' ? 'tipai' : 
                    name === 'atsakymai-total' ? 'atsakymaiTotal' : null;

    // Funkcija, kuri laukų pavadinimus pritaiko Supabase formatui
    const formatFieldNames = useCallback((data) => {
        const fieldMap = {
            // Pagrindiniai formos laukai
            'formosPavadinimas': 'formos_pavadinimas',
            'imonesPavadinimas': 'imones_pavadinimas',
            'formObject': 'form_object',
            'kasPildo': 'kas_pildo',
            'validTo': 'valid_to',
            'createdAt': 'created_at',
            'updatedAt': 'updated_at',
            'uzpildymoData': 'uzpildymo_data',
            'slaptazodis': 'slaptazodis',
            'formosTipaiId': 'formos_tipai_id',
            'userId': 'user_id',
            
            // Naudotojų laukai
            'adminConfirmed': 'admin_confirmed',
            'resetPasswordToken': 'reset_password_token',
            'confirmationToken': 'confirmation_token',
            'confirmed': 'confirmed',
            
            // Kiti laukai
            'imone': 'imone',
            'tipas': 'tipas',
            'pavadinimas': 'pavadinimas',
            'aprasymas': 'aprasymas',
            'aktyvus': 'aktyvus',
            'data': 'data',
            'atsakymas': 'atsakymas',
            'klausimas': 'klausimas',
            'vertinimas': 'vertinimas',
            'komentaras': 'komentaras',
            
            // Tinkamai apdorojame CamelCase variacijoms
            'formosObject': 'form_object',
            'formosPav': 'formos_pavadinimas',
            'imonesPav': 'imones_pavadinimas'
        };

        // Optimizuota transformacija su apdorojimu JSON laukų
        const transformObject = (obj) => {
            const result = {};
            for (const key in obj) {
                // Ignoruojame id laukus naujų įrašų kūrimuose, kad išvengtume unique constraint problemų
                if (key === 'id') {
                    continue;
                }
                
                // Jei raktas jau yra snake_case, nekeisti
                if (key.includes('_')) {
                    // Bet vis tiek ignoruojame 'id' lauką, net jei jis yra su _
                    if (key !== 'id') {
                        result[key] = obj[key];
                    }
                    continue;
                }
                
                const newKey = fieldMap[key] || key;
                // Dar kartą patikriname ar tai ne id laukas
                if (newKey !== 'id') {
                    result[newKey] = obj[key];
                }
            }
            return result;
        };

        if (Array.isArray(data)) {
            return data.map(transformObject);
        }
        
        const transformed = transformObject(data);
        return transformed;
    }, []);

    // Funkcija, kuri konvertuoja Supabase duomenis į Strapi formatą
    const convertToStrapiFormat = useCallback((data) => {
        if (!data || data.length === 0) return [];
        
        // Atvirkštinis fieldMap
        const reverseFieldMap = {
            'formos_pavadinimas': 'formosPavadinimas',
            'imones_pavadinimas': 'imonesPavadinimas',
            'form_object': 'formObject',
            'kas_pildo': 'kasPildo',
            'valid_to': 'validTo',
            'created_at': 'createdAt',
            'updated_at': 'updatedAt',
            'uzpildymo_data': 'uzpildymoData'
        };

        // Optimizuota transformacija
        return data.map(item => ({
            id: String(item.id), // Convert to string to avoid JavaScript number precision issues
            attributes: Object.entries(item).reduce((acc, [key, value]) => {
                if (key === 'id') return acc;
                const newKey = reverseFieldMap[key] || key;
                acc[newKey] = value;
                return acc;
            }, {})
        }));
    }, []);

    // Patikriname, ar jau turime duomenis cache
    useEffect(() => {
        if (dataKey && isDataLoaded && originalData[dataKey]?.length > 0) {
            setData(originalData[dataKey]);
            setLoading(false); 
        }
    }, [dataKey, isDataLoaded, originalData]);

    // Optimizuotas duomenų gavimas su geresniu cache ir filtravimo logika
    const getAll = useCallback(async (filters = {}, options = {}) => {
        // Increased page size for faster loading
        const { page = 0, pageSize = 500, onProgress, loadInBatches = false } = options;
        
        // Jei nekrauname batch'ais ir turime cache, naudojame jį
        if (!loadInBatches && dataKey && isDataLoaded && 
            Object.keys(filters).length === 0 && originalData[dataKey]?.length > 0) {
            setData(originalData[dataKey]);
            return originalData[dataKey];
        }

        // Nauja optimizacija - naudojam filtravimo saltų stringą hash palyginimui
        const filterHash = JSON.stringify(filters);
        const cacheKey = `${name}_${filterHash}`;
        
        // Jei jau esame užklausę tokią pačią filtruotą duomenų aibę neseniai, 
        // gražiname ją iš lokalaus cache
        if (sessionStorage.getItem(cacheKey)) {
            try {
                const cachedResults = JSON.parse(sessionStorage.getItem(cacheKey));
                setData(cachedResults);
                return cachedResults;
            } catch (error) {
                // Jei nepavyko ištraukti iš cache, tęsiame su įprasta užklausa
                sessionStorage.removeItem(cacheKey);
            }
        }

        setLoading(true);
        setError(null);

        try {
            let allData = [];
            let hasMore = true;
            let currentPage = page;
            let totalCount = 0;
            let actualPageSize = pageSize; // Prisitaikantis puslapio dydis
            let retryDelay = 1000; // Start with 1 second delay
            
            // Formatuojame visus filtrus iš anksto
            const formattedFilters = {};
            Object.entries(filters).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    const formattedKey = key.includes('_') ? key : formatFieldNames({ [key]: null })[key];
                    if (formattedKey) {
                        formattedFilters[formattedKey] = value;
                    }
                }
            });
            
            // Pirmiausia bandyme gauti bendrą įrašų skaičių, kad žinotume ar yra prasmė krauti visus puslapius
            try {
                let countQuery = supabase.from(name)
                    .select('*', { count: 'exact', head: true }); // head: true reiškia, kad negrąžins duomenų, tik kiekį
                
                // Pritaikome visus filtrus iš karto
                Object.entries(formattedFilters).forEach(([key, value]) => {
                    countQuery = countQuery.eq(key, value);
                });
                
                const { count, error: countError } = await requestQueue.execute(() => countQuery);
                
                if (!countError && count !== null) {
                    totalCount = count;
                    // Jei nėra jokių duomenų, iškart grąžiname tuščią masyvą
                    if (totalCount === 0) {
                        setLoading(false);
                        setData([]);
                        return [];
                    }
                }
            } catch (countError) {
                // Even if count fails, we'll still try to load data
            }
            
            // Krauname po puslapį, kol turime duomenų
            while (hasMore) {
                // Tarp užklausų padarome pauzę - mažinama serverio apkrova
                if (currentPage > 0) {
                    await new Promise(resolve => setTimeout(resolve, retryDelay));
                }
                let query = supabase.from(name)
                    .select('*', { count: 'exact' });
                
                // Pritaikome visus filtrus iš karto
                Object.entries(formattedFilters).forEach(([key, value]) => {
                    query = query.eq(key, value);
                });
                
                try {
                    // Patikrinkime ar dabartinis offset neviršija turimo duomenų skaičiaus
                    if (totalCount > 0 && currentPage * actualPageSize >= totalCount) {
                        hasMore = false;
                        break;
                    }
                    
                    // Subabase užklausa su range dabar įvykdoma per queue sistemą
                    const finalQuery = query.range(
                        currentPage * actualPageSize, 
                        (currentPage + 1) * actualPageSize - 1
                    );
                    
                    const { data: responseData, error: responseError, count: responseCount } = 
                        await requestQueue.execute(() => finalQuery);
                    
                    if (responseError) {
                        // Jei klaida susijusi su užklausos ribomis, reiškia pasiekėme pabaigą
                        if (responseError.code === 'PGRST103') {
                            hasMore = false;
                            break;
                        }
                        throw responseError;
                    }

                    // Jei suradome totalCount, jį išsaugome
                    if (responseCount !== null) {
                        totalCount = responseCount;
                    }
                    
                    const formattedBatch = convertToStrapiFormat(responseData || []);
                    allData = [...allData, ...formattedBatch];

                    // Reset retry delay after success
                    retryDelay = 1000;
                    
                    // Progressively decrease delay after successful requests
                    retryDelay = Math.max(1000, retryDelay * 0.8);

                    // Jei krauname batch'ais, atnaujiname progress
                    if (options.loadInBatches) {
                        setData(prevData => [...prevData, ...formattedBatch]);
                        if (onProgress && totalCount > 0) {
                            onProgress(allData.length, totalCount);
                        }
                    }

                    // Patikrinkime ar yra daugiau duomenų
                    hasMore = responseData && responseData.length === actualPageSize;
                    currentPage++;
                } catch (batchError) {
                    
                    // Jei gaunamas 500 serverio klaida arba timeout, sumažiname puslapio dydį
                    if ((batchError.code === '500' || batchError.code === '57014') && actualPageSize > 100) {
                        // Mažiname puslapio dydį agresyviau nei anksčiau
                        actualPageSize = Math.max(50, Math.floor(actualPageSize * 0.5));
                        
                        // Didinama pauzė tarp užklausų su eksponentine backoff strategija
                        retryDelay = Math.min(15000, retryDelay * 2);
                        
                        await new Promise(resolve => setTimeout(resolve, retryDelay));
                        
                        // Nebandome sekančio puslapio - bandome vėl tą patį
                        continue;
                    }
                    
                    // Jei klaida nėra susijusi su puslapio dydžiu, tiesiog bandome sekantį puslapį
                    currentPage++;
                    
                    // Didinamos pauzės tarp užklausų
                    retryDelay = Math.min(10000, retryDelay * 1.5);
                    await new Promise(resolve => setTimeout(resolve, retryDelay));
                    
                    // Bandome ne daugiau 10 puslapių iš viso, jei išlieka problemos
                    if (currentPage > 10) { 
                        console.warn('Reached max page attempts, stopping data loading');
                        hasMore = false;
                    }
                }
                
                // Šis kodas nebereikalingas - pauzės valdamos naujojoje sistemoje
            }

            // Jei surinkome mažiau nei 50% duomenų, pranešame apie nepilnus duomenis
            if (totalCount > 0 && allData.length < totalCount * 0.5) {
                console.warn(`Incomplete data: collected ${allData.length} out of ${totalCount} records`);
            }
            
            // Atnaujiname globalų state tik jei filtrai tušti (t.y. tai yra pilni duomenys)
            if (dataKey && Object.keys(filters).length === 0) {
                setOriginalData({
                    [dataKey]: allData
                });
            }
            
            // Saugome rezultatus į sesijos cache 15 minučių
            if (cacheKey && allData.length > 0) {
                try {
                    sessionStorage.setItem(cacheKey, JSON.stringify(allData));
                    
                    // Apsivalome seni mėnesiniai rezultatai po 15 minučių
                    setTimeout(() => {
                        sessionStorage.removeItem(cacheKey);
                    }, 15 * 60 * 1000); // 15 minučių
                } catch (error) {
                    setError('Nepavyko įrašyti į cache');
                }
            }
            
            // Nustatome galutinius duomenis
            if (!options.loadInBatches) {
                setData(allData);
            }
            
            setLoading(false);
            return allData;
        } catch (error) {
            setError('Bendras duomenų gavimo klaida');
            setLoading(false);
            return [];
        }
    }, [name, dataKey, isDataLoaded, originalData, setOriginalData, formatFieldNames, convertToStrapiFormat]);

    // Funkcija vieno įrašo gavimui pagal ID
    const getSingle = useCallback(async (id, setterFunction = null) => {
        if (!id) return null;
        
        setLoading(true);
        setError(null);
        
        try {
            const { data: responseData, error: responseError } = await supabase
                .from(name)
                .select('*')
                .eq('id', id)
                .single();
            
            if (responseError) throw responseError;
            
            const formattedData = convertToStrapiFormat([responseData])[0];
            
            if (setterFunction && typeof setterFunction === 'function') {
                setterFunction(formattedData);
            }
            
            setLoading(false);
            return formattedData;
        } catch (error) {
            setError('Klaida gaunant įrašą');
            setLoading(false);
            return null;
        }
    }, [name, convertToStrapiFormat]);

    // Funkcija įrašo atnaujinimui
    const update = useCallback(async (id, updateData) => {
        if (!id || !updateData) return null;
        
        setLoading(true);
        setError(null);
        
        try {
            const formattedData = formatFieldNames(updateData);
            
            const { data: responseData, error: responseError } = await supabase
                .from(name)
                .update(formattedData)
                .eq('id', id)
                .select();
            
            if (responseError) throw responseError;
            
            const updatedData = convertToStrapiFormat(responseData)[0];
            
            // Atnaujinti globalų state jei reikia
            if (dataKey) {
                setOriginalData(prev => {
                    const updated = prev[dataKey]?.map(item => 
                        item.id === id ? updatedData : item
                    ) || [];
                    return { ...prev, [dataKey]: updated };
                });
            }
            
            setLoading(false);
            return updatedData;
        } catch (error) {
            setError('Klaida atnaujinant įrašą');
            setLoading(false);
            return null;
        }
    }, [name, dataKey, formatFieldNames, convertToStrapiFormat, setOriginalData]);

    // Funkcija įrašo sukūrimui
    const create = useCallback(async (createData) => {
        if (!createData) {
            console.error('No data provided for create operation');
            return null;
        }
        
        setLoading(true);
        setError(null);
        
        try {
            const formattedData = formatFieldNames(createData);
            
            // Jei ID yra nurodytas ir yra 0 arba null, pašaliname jį, kad DB galėtų sugeneruoti
            if ('id' in formattedData && (formattedData.id === 0 || formattedData.id == null)) {
                delete formattedData.id;
            }
            
            // Remove client-side only or read-only fields before sending
            const fieldsToRemove = ['created_at', 'updated_at']; // Add other fields if needed
            fieldsToRemove.forEach(field => delete formattedData[field]);
            
            const { data: responseData, error } = await supabase
                .from(name)
                .insert(formattedData)
                .select();
            
            if (error) {
                console.error('Supabase insert error:', error);
                throw error;
            }
            
            const newData = convertToStrapiFormat(responseData)[0];
            
            // Atnaujinti globalų state jei reikia
            if (dataKey) {
                setOriginalData(prev => {
                    return { 
                        ...prev, 
                        [dataKey]: [...(prev[dataKey] || []), newData] 
                    };
                });
            }
            
            setLoading(false);
            return newData;
        } catch (error) {
            console.error('Error creating new record:', error);
            setError('Klaida kuriant naują įrašą: ' + (error.message || error));
            setLoading(false);
            return null;
        }
    }, [name, dataKey, formatFieldNames, convertToStrapiFormat, setOriginalData]);

    // Funkcija įrašo ištrynimui
    const remove = useCallback(async (id) => {
        if (!id) return false;
        
        setLoading(true);
        setError(null);
        
        try {
            const { error: responseError } = await supabase
                .from(name)
                .delete()
                .eq('id', id);
            
            if (responseError) throw responseError;
            
            // Atnaujinti globalų state jei reikia
            if (dataKey) {
                setOriginalData(prev => {
                    const filtered = prev[dataKey]?.filter(item => item.id !== id) || [];
                    return { ...prev, [dataKey]: filtered };
                });
            }
            
            setLoading(false);
            return true;
        } catch (error) {
            setError('Klaida trinant įrašą');
            setLoading(false);
            return false;
        }
    }, [name, dataKey, setOriginalData]);

    // Funkcija įrašo gavųmo limito nustatymui
    const rowcount = useCallback(async (filters = {}) => {
        try {
            const formattedFilters = {};
            Object.entries(filters).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    const formattedKey = key.includes('_') ? key : formatFieldNames({ [key]: null })[key];
                    if (formattedKey) {
                        formattedFilters[formattedKey] = value;
                    }
                }
            });

            let query = supabase.from(name).select('*', { count: 'exact', head: true });
            
            // Pritaikome visus filtrus iš karto
            Object.entries(formattedFilters).forEach(([key, value]) => {
                query = query.eq(key, value);
            });
            
            const { count, error } = await query;
            
            if (error) throw error;
            return count || 0;
        } catch (error) {
            setError('Klaida gaunant įrašų kiekį');
            return 0;
        }
    }, [name, formatFieldNames]);

    return {
        data,
        isLoading,
        error,
        getAll,
        getSingle,
        update,
        create,
        remove,
        rowcount,
        setData,
    };
}

export default useSupabaseData;
